import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ArrowLeft, Sparkles, Home, Trees, Coffee, BookOpen, Gamepad2, Film, Shirt as ShirtIcon, Heart } from 'lucide-react';
export function BarndormatoryPage() {
  return <div className="min-h-screen font-sans" style={{
    backgroundColor: '#0D121D'
  }} data-id="element-197">
      <Header data-id="element-198" />

      <main data-id="element-199">
        {/* Hero Section with Barn Exterior */}
        <section className="relative pt-32 pb-32 overflow-hidden" style={{
        backgroundImage: 'linear-gradient(rgba(13, 18, 29, 0.75), rgba(13, 18, 29, 0.95)), url(https://cdn.magicpatterns.com/uploads/9mMW27wzQozbJryU4wuK2Q/HOlpD.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }} data-id="element-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-201">
            <a href="/" className="inline-flex items-center text-slate-300 hover:text-white transition-colors mb-12" data-id="element-202">
              <ArrowLeft className="w-5 h-5 mr-2" data-id="element-203" />
              Back to Home
            </a>

            <div className="text-center max-w-4xl mx-auto" data-id="element-204">
              <div className="mb-10 flex justify-center" data-id="element-205">
                <img src="https://cdn.magicpatterns.com/uploads/8BjGD32ieUY3EYzqm5M2xt/Twice_Bourne_Logo-09.png" alt="Twice Bourne Barndormitory Holdings LLC Logo" className="max-w-sm w-full h-auto" data-id="element-206" />
              </div>

              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-300 text-sm font-bold mb-6 uppercase tracking-wider" data-id="element-207">
                <Sparkles className="w-4 h-4" data-id="element-208" />
                Vision in Development
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight drop-shadow-2xl" data-id="element-209">
                Twice Bourne Barndormitory Holdings LLC
              </h1>

              <p className="text-xl md:text-2xl text-slate-200 mb-4 leading-relaxed drop-shadow-lg" data-id="element-210">
                Reimagining elderly care from the ground up.
              </p>
              <p className="text-lg text-slate-300 max-w-2xl mx-auto drop-shadow-lg" data-id="element-211">
                A complete community — built indoors, designed with dignity,
                wrapped in the warmth of a barn.
              </p>
            </div>
          </div>
        </section>

        {/* Vision Statement */}
        <section className="py-20" data-id="element-212">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-213">
            <div className="bg-white/5 backdrop-blur rounded-3xl border border-amber-500/20 p-10 md:p-14" data-id="element-214">
              <div className="flex items-center gap-3 mb-6" data-id="element-215">
                <div className="w-12 h-12 rounded-full bg-amber-400/20 flex items-center justify-center" data-id="element-216">
                  <Heart className="w-6 h-6 text-amber-300" data-id="element-217" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white" data-id="element-218">
                  The Vision
                </h2>
              </div>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-4" data-id="element-219">
                What if elderly care didn't feel like a facility — but felt like{' '}
                <span className="text-amber-300 font-semibold" data-id="element-220">home</span>?
              </p>
              <p className="text-lg text-slate-400 leading-relaxed" data-id="element-221">
                Twice Bourne Barndormitory is a bold concept to elevate elderly
                living into something truly special. From the outside, it
                resembles a classic barn nestled in the countryside. On the
                inside, it's a thriving, walkable community — where hallways
                become charming streets, private rooms become cozy houses, and
                every shared space invites connection, comfort, and joy.
              </p>
            </div>
          </div>
        </section>

        {/* Streets and Houses Section */}
        <section className="py-12" data-id="element-222">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-223">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-224">
              <div className="order-2 lg:order-1" data-id="element-225">
                <div className="inline-block px-3 py-1 rounded-full bg-amber-400/20 text-amber-300 text-xs font-bold mb-4 uppercase tracking-wider" data-id="element-226">
                  Hallways as Streets
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-227">
                  A Neighborhood Inside
                </h2>
                <p className="text-lg text-slate-300 leading-relaxed mb-4" data-id="element-228">
                  Wander down a softly lit lane lined with flower boxes and warm
                  lanterns. Wave to a neighbor. Stop for a chat. Inside
                  Barndormitory, every hallway is designed to feel like a
                  village street — and every door opens into a residence that
                  feels truly personal.
                </p>
                <p className="text-lg text-slate-400 leading-relaxed" data-id="element-229">
                  Residents don't live in <em data-id="element-230">rooms</em>. They live in{' '}
                  <span className="text-amber-300" data-id="element-231">homes</span>.
                </p>
              </div>

              <div className="order-1 lg:order-2" data-id="element-232">
                <div className="rounded-3xl overflow-hidden border border-amber-500/20 shadow-2xl" data-id="element-233">
                  <img src="https://cdn.magicpatterns.com/uploads/5qi2Zdb3D47qDz5RNEARTm/JP9If.jpg" alt="Indoor hallway designed as a community street" className="w-full h-auto" data-id="element-234" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Plaza Section */}
        <section className="py-12" data-id="element-235">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-236">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-237">
              <div data-id="element-238">
                <div className="rounded-3xl overflow-hidden border border-amber-500/20 shadow-2xl" data-id="element-239">
                  <img src="https://cdn.magicpatterns.com/uploads/qr9UdnMMGJXk5jnfCK69T7/Barndormitory_Plaza.jpg" alt="Central plaza with fountain and skylight" className="w-full h-auto" data-id="element-240" />
                </div>
              </div>

              <div data-id="element-241">
                <div className="inline-block px-3 py-1 rounded-full bg-amber-400/20 text-amber-300 text-xs font-bold mb-4 uppercase tracking-wider" data-id="element-242">
                  The Heart of the Community
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-243">
                  A Plaza to Gather
                </h2>
                <p className="text-lg text-slate-300 leading-relaxed mb-4" data-id="element-244">
                  At the center of it all is a grand indoor plaza — open,
                  bright, and filled with life. Sunlight streams through
                  skylights. A fountain bubbles gently. Greenery and flowers
                  bloom year-round.
                </p>
                <p className="text-lg text-slate-400 leading-relaxed" data-id="element-245">
                  This is where neighbors meet, friendships grow, and every day
                  feels a little bit like a celebration.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Amenities Grid */}
        <section className="py-20" data-id="element-246">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-247">
            <div className="text-center mb-12" data-id="element-248">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" data-id="element-249">
                A Complete Way of Life
              </h2>
              <p className="text-lg text-slate-400 max-w-2xl mx-auto" data-id="element-250">
                Every amenity you'd find in a vibrant town — thoughtfully
                designed and right at home.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-251">
              <div className="bg-white/5 backdrop-blur rounded-2xl border border-slate-700/50 hover:border-amber-400/50 transition-colors p-8" data-id="element-252">
                <div className="w-14 h-14 rounded-full bg-amber-400/20 flex items-center justify-center mb-5" data-id="element-253">
                  <Home className="w-7 h-7 text-amber-300" data-id="element-254" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-255">
                  Personal Residences
                </h3>
                <p className="text-slate-400" data-id="element-256">
                  Private rooms reimagined as cozy homes, each with its own
                  character and warmth.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur rounded-2xl border border-slate-700/50 hover:border-amber-400/50 transition-colors p-8" data-id="element-257">
                <div className="w-14 h-14 rounded-full bg-amber-400/20 flex items-center justify-center mb-5" data-id="element-258">
                  <Trees className="w-7 h-7 text-amber-300" data-id="element-259" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-260">
                  Central Plaza
                </h3>
                <p className="text-slate-400" data-id="element-261">
                  A bright, open gathering space at the heart of the community.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur rounded-2xl border border-slate-700/50 hover:border-amber-400/50 transition-colors p-8" data-id="element-262">
                <div className="w-14 h-14 rounded-full bg-amber-400/20 flex items-center justify-center mb-5" data-id="element-263">
                  <ShirtIcon className="w-7 h-7 text-amber-300" data-id="element-264" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-265">
                  Laundromat
                </h3>
                <p className="text-slate-400" data-id="element-266">
                  Modern, accessible laundry facilities right within the
                  community.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur rounded-2xl border border-slate-700/50 hover:border-amber-400/50 transition-colors p-8" data-id="element-267">
                <div className="w-14 h-14 rounded-full bg-amber-400/20 flex items-center justify-center mb-5" data-id="element-268">
                  <BookOpen className="w-7 h-7 text-amber-300" data-id="element-269" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-270">Library</h3>
                <p className="text-slate-400" data-id="element-271">
                  A quiet, welcoming space to read, learn, and reflect.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur rounded-2xl border border-slate-700/50 hover:border-amber-400/50 transition-colors p-8" data-id="element-272">
                <div className="w-14 h-14 rounded-full bg-amber-400/20 flex items-center justify-center mb-5" data-id="element-273">
                  <Coffee className="w-7 h-7 text-amber-300" data-id="element-274" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-275">
                  Activity Rooms
                </h3>
                <p className="text-slate-400" data-id="element-276">
                  Dedicated spaces for hobbies, classes, crafts, and creative
                  expression.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur rounded-2xl border border-slate-700/50 hover:border-amber-400/50 transition-colors p-8" data-id="element-277">
                <div className="w-14 h-14 rounded-full bg-amber-400/20 flex items-center justify-center mb-5" data-id="element-278">
                  <Gamepad2 className="w-7 h-7 text-amber-300" data-id="element-279" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-280">Game Room</h3>
                <p className="text-slate-400" data-id="element-281">
                  Cards, board games, and fun gatherings to keep the spirit
                  young.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur rounded-2xl border border-slate-700/50 hover:border-amber-400/50 transition-colors p-8 sm:col-span-2 lg:col-span-1" data-id="element-282">
                <div className="w-14 h-14 rounded-full bg-amber-400/20 flex items-center justify-center mb-5" data-id="element-283">
                  <Film className="w-7 h-7 text-amber-300" data-id="element-284" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-285">
                  Mini Cinema
                </h3>
                <p className="text-slate-400" data-id="element-286">
                  Movie nights and shared screenings — the magic of the theater,
                  steps away.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Closing / Status Section */}
        <section className="pb-24" data-id="element-287">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-288">
            <div className="bg-gradient-to-br from-amber-500/20 via-amber-600/10 to-slate-900/40 backdrop-blur rounded-3xl border border-amber-400/30 p-12 text-center" data-id="element-289">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-400/20 border border-amber-300/40 text-amber-300 text-sm font-bold mb-6 uppercase tracking-wider" data-id="element-290">
                <Sparkles className="w-4 h-4" data-id="element-291" />
                Concept Stage
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-292">
                The Future of Elderly Living
              </h2>
              <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed" data-id="element-293">
                Twice Bourne Barndormitory Holdings LLC is currently in the
                vision and concept stage. We're building the foundation for a
                new standard of elderly care — one that honors dignity, fosters
                community, and feels like home.
              </p>
              <p className="text-base text-slate-400 mt-4" data-id="element-294">
                More updates coming as the vision takes shape.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-295" />
    </div>;
}